import { Routes } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { ngxPermissionsGuard } from 'ngx-permissions';
import { crudGuard, detailGuard } from '~ngx-shared/guards';
import { breadcrumbResolver, crudResolver } from '~ngx-shared/resolvers';
import { AuthorizationUtil } from '~shared/utils';
import { CanAccessPersonDetailGuard } from './guards/can-access-farabi.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/layout.component').then(m => m.LayoutComponent),
    providers: [provideTranslocoScope('shared'), provideTranslocoScope('community')],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      },
      /******************
       * HOME
       ******************/
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard-page/dashboard-page.component').then(
            m => m.DashboardPageComponent
          ),
        data: {
          breadcrumb: 'dashboard'
        }
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile-page/profile-page.component').then(m => m.ProfilePageComponent),
        data: {
          breadcrumb: 'profile'
        }
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings-page/settings-page.component').then(
            m => m.SettingsPageComponent
          ),
        data: {
          breadcrumb: 'settings'
        }
      },
      /******************
       * COMMUNITIES
       ******************/
      {
        path: 'communities',
        data: {
          breadcrumb: 'shared.communities'
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/community-list-page/community-list-page.component').then(
                m => m.CommunityListPageComponent
              )
          },
          {
            path: 'detail/:community-id',
            loadComponent: () =>
              import('./pages/community-detail-page/community-detail-page.component').then(
                m => m.CommunityDetailPageComponent
              ),
            canActivate: [detailGuard('community-id')],
            resolve: { crud: crudResolver('community-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.community'
            }
          },
          {
            path: 'community-function-people/:community-id',
            loadComponent: () =>
              import('./pages/create-update-community-function-person-form-page/create-update-community-function-person-form-page.component').then(
                m => m.CreateUpdateCommunityFunctionPersonFormPageComponent
              ),
            canActivate: [ngxPermissionsGuard, crudGuard('community-id')],
            resolve: {
              crud: crudResolver('community-id'),
              breadcrumb: breadcrumbResolver('community-id')
            },
            data: {
              breadcrumbParamValue: 'shared.community_function',
              permissions: {
                only: AuthorizationUtil.GROUP_RELIGIOUS,
                redirectTo: '/'
              }
            }
          },
          {
            path: ':community-id',
            loadComponent: () =>
              import('~shared/community/pages/create-community-data-form-page/create-community-data-form-page.component').then(
                m => m.CreateCommunityDataFormPageComponent
              ),
            canActivate: [ngxPermissionsGuard, crudGuard('community-id')],
            resolve: {
              crud: crudResolver('community-id'),
              breadcrumb: breadcrumbResolver('community-id')
            },
            data: {
              breadcrumbParamValue: 'shared.community',
              permissions: {
                only: AuthorizationUtil.GROUP_RELIGIOUS,
                redirectTo: '/'
              }
            }
          }
        ]
      },
      {
        path: 'deadlines',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./pages/deadlines-page/deadlines-page.component').then(
            m => m.DeadlinesPageComponent
          ),
        data: {
          breadcrumb: 'community.deadlines',
          icon: 'pi pi-clock',
          permissions: {
            only: AuthorizationUtil.GROUP_COMMUNITY_MANAGEMENT,
            redirectTo: '/'
          }
        }
      },
      {
        path: 'religious-communities',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./pages/religious-community-list-page/religious-community-list-page.component').then(
            m => m.ReligiousCommunityListPageComponent
          ),
        data: {
          breadcrumb: 'shared.community_type.religious_communities',
          permissions: {
            only: AuthorizationUtil.GROUP_RELIGIOUS,
            redirectTo: '/'
          }
        }
      },
      {
        path: 'cultic-communities',
        loadComponent: () =>
          import('./pages/cultic-community-list-page/cultic-community-list-page.component').then(
            m => m.CulticCommunityListPageComponent
          ),
        data: {
          breadcrumb: 'shared.community_type.cultic_communities'
        }
      },
      {
        path: 'mosque-facilities',
        loadComponent: () =>
          import('./pages/mosque-facility-community-list-page/mosque-facility-community-list-page.component').then(
            m => m.MosqueFacilityCommunityListPageComponent
          ),
        data: {
          breadcrumb: 'shared.community_type.mosque_facilities'
        }
      },
      {
        path: 'mosques',
        loadComponent: () =>
          import('./pages/mosque-community-list-page/mosque-community-list-page.component').then(
            m => m.MosqueCommunityListPageComponent
          ),
        data: {
          breadcrumb: 'shared.community_type.mosques'
        }
      },
      {
        path: 'professional-associations',
        loadComponent: () =>
          import('./pages/professional-association-community-list-page/professional-association-community-list-page.component').then(
            m => m.ProfessionalAssociationCommunityListPageComponent
          ),
        data: {
          breadcrumb: 'shared.community_type.professional_associations'
        }
      },
      {
        path: 'prayer-rooms',
        canActivate: [ngxPermissionsGuard],
        loadComponent: () =>
          import('./pages/prayer-room-list/prayer-room-list.component').then(
            m => m.PrayerRoomListComponent
          ),
        data: {
          breadcrumb: 'shared.community_type.prayer_rooms',
          permissions: {
            only: AuthorizationUtil.GROUP_RELIGIOUS,
            redirectTo: '/'
          }
        }
      },
      /******************
       * EVENTS
       ******************/
      {
        path: 'events',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.events',
          permissions: {
            only: AuthorizationUtil.GROUP_COMMUNITY_MANAGEMENT,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/event-list-page/event-list-page.component').then(
                m => m.EventListPageComponent
              )
          },
          {
            path: 'detail/:event-id',
            loadComponent: () =>
              import('./pages/event-detail-page/event-detail-page.component').then(
                m => m.EventDetailPageComponent
              ),
            canActivate: [detailGuard('event-id')],
            resolve: { crud: crudResolver('event-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'shared.event'
            }
          },
          {
            path: ':event-id',
            loadComponent: () =>
              import('./pages/create-update-event-form-page/create-update-event-form-page.component').then(
                m => m.CreateUpdateEventFormPageComponent
              ),
            canActivate: [crudGuard('event-id')],
            resolve: {
              crud: crudResolver('event-id'),
              breadcrumb: breadcrumbResolver('event-id')
            },
            data: {
              breadcrumbParamValue: 'shared.event'
            }
          }
        ]
      },
      /******************
       * TOM
       ******************/
      {
        path: 'tom',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'community.tom',
          permissions: {
            only: AuthorizationUtil.GROUP_COMMUNITY_MANAGEMENT,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/tom-event-list-page/tom-event-list-page.component').then(
                m => m.TomEventListPageComponent
              )
          },
          {
            path: 'detail/:tom-event-id',
            loadComponent: () =>
              import('./pages/tom-event-detail-page/tom-event-detail-page.component').then(
                m => m.TomEventDetailPageComponent
              ),
            canActivate: [detailGuard('tom-event-id')],
            resolve: { crud: crudResolver('tom-event-id') },
            data: {
              breadcrumb: 'detail_value',
              breadcrumbParamValue: 'community.tom'
            }
          },
          {
            path: ':tom-event-id',
            loadComponent: () =>
              import('./pages/create-update-tom-event-form-page/create-update-tom-event-form-page.component').then(
                m => m.CreateUpdateTomEventFormPageComponent
              ),
            canActivate: [ngxPermissionsGuard, crudGuard('tom-event-id')],
            resolve: {
              crud: crudResolver('tom-event-id'),
              breadcrumb: breadcrumbResolver('tom-event-id')
            },
            data: {
              breadcrumbParamValue: 'community.tom',
              permissions: {
                only: AuthorizationUtil.GROUP_OFFICE,
                redirectTo: '/'
              }
            }
          }
        ]
      },
      /******************
       * PROTOCOLS
       ******************/
      {
        path: 'protocols',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.protocols',
          permissions: {
            only: AuthorizationUtil.GROUP_RELIGIOUS,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'religious-communities',
            data: {
              breadcrumb: 'shared.meeting_minutes_religious_communities'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/protocol-religious-community-list-page/protocol-religious-community-list-page.component').then(
                    m => m.ProtocolReligiousCommunityListPageComponent
                  )
              },
              {
                path: ':protocol-religious-community-id',
                loadComponent: () =>
                  import('./pages/create-update-protocol-religious-community-form-page/create-update-protocol-religious-community-form-page.component').then(
                    m => m.CreateUpdateProtocolReligiousCommunityFormPageComponent
                  ),
                canActivate: [crudGuard('protocol-religious-community-id')],
                resolve: {
                  crud: crudResolver('protocol-religious-community-id'),
                  breadcrumb: breadcrumbResolver('protocol-religious-community-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.meeting_minute_religious_communities'
                }
              }
            ]
          }
        ]
      },
      /******************
       * ACTORS
       ******************/
      {
        path: 'actors',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.actors',
          permissions: {
            only: AuthorizationUtil.GROUP_OFFICE,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'people',
            data: {
              breadcrumb: 'shared.people',
              icon: 'pi pi-users'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/person-list-page/person-list-page.component').then(
                    m => m.PersonListPageComponent
                  )
              },
              {
                path: 'detail/:person-id',
                loadComponent: () =>
                  import('./pages/person-detail-page/person-detail-page.component').then(
                    m => m.PersonDetailPageComponent
                  ),
                canActivate: [detailGuard('person-id')],
                resolve: { crud: crudResolver('person-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.person'
                }
              },
              {
                path: 'bulk-import',
                loadComponent: () =>
                  import('~shared/core/pages/create-update-person-bulk-page/create-update-person-bulk-page.component').then(
                    m => m.CreateUpdatePersonBulkPageComponent
                  ),
                data: {
                  breadcrumb: 'shared.bulk_import'
                }
              },
              {
                path: ':person-id',
                loadComponent: () =>
                  import('~shared/core/pages/create-person-data-form-page/create-person-data-form-page.component').then(
                    m => m.CreatePersonDataFormPageComponent
                  ),
                canActivate: [crudGuard('person-id')],
                resolve: {
                  crud: crudResolver('person-id'),
                  breadcrumb: breadcrumbResolver('person-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.person'
                }
              }
            ]
          },
          {
            path: 'community-offices',
            data: {
              breadcrumb: 'shared.role.community_offices',
              icon: 'pi pi-fw pi-briefcase'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/community-office-list-page/community-office-list-page.component').then(
                    m => m.CommunityOfficeListPageComponent
                  )
              },
              {
                path: 'detail/:community-office-id',
                redirectTo: '/actors/people/detail/:community-office-id'
              },
              {
                path: ':community-office-id',
                loadComponent: () =>
                  import('./pages/create-community-office-data-form-page/create-community-office-data-form-page.component').then(
                    m => m.CreateCommunityOfficeDataFormPageComponent
                  ),
                canActivate: [crudGuard('community-office-id')],
                resolve: {
                  crud: crudResolver('community-office-id'),
                  breadcrumb: breadcrumbResolver('community-office-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.community_office'
                }
              }
            ]
          },
          {
            path: 'community-religious',
            data: {
              breadcrumb: 'shared.role.community_religious',
              icon: 'pi pi-fw pi-briefcase'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/community-religious-list-page/community-religious-list-page.component').then(
                    m => m.CommunityReligiousListPageComponent
                  )
              },
              {
                path: 'detail/:community-religious-id',
                redirectTo: '/actors/people/detail/:community-religious-id'
              },
              {
                path: ':community-religious-id',
                loadComponent: () =>
                  import('./pages/create-community-religious-data-form-page/create-community-religious-data-form-page.component').then(
                    m => m.CreateCommunityReligiousDataFormPageComponent
                  ),
                canActivate: [crudGuard('community-religious-id')],
                resolve: {
                  crud: crudResolver('community-religious-id'),
                  breadcrumb: breadcrumbResolver('community-religious-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.community_religious'
                }
              }
            ]
          },
          {
            path: 'community-responsible',
            data: {
              breadcrumb: 'shared.role.community_responsible',
              icon: 'pi pi-fw pi-briefcase'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/community-responsible-list-page/community-responsible-list-page.component').then(
                    m => m.CommunityResponsibleListPageComponent
                  )
              },
              {
                path: 'detail/:community-responsible-id',
                redirectTo: '/actors/people/detail/:community-responsible-id'
              },
              {
                path: ':community-responsible-id',
                loadComponent: () =>
                  import('./pages/create-community-responsible-data-form-page/create-community-responsible-data-form-page.component').then(
                    m => m.CreateCommunityResponsibleDataFormPageComponent
                  ),
                canActivate: [crudGuard('community-responsible-id')],
                resolve: {
                  crud: crudResolver('community-responsible-id'),
                  breadcrumb: breadcrumbResolver('community-responsible-id')
                },
                data: {
                  breadcrumbParamValue: 'shared.role.community_responsible'
                }
              }
            ]
          }
        ]
      },
      /******************
       * FARABI
       ******************/
      {
        path: 'farabi',
        canActivate: [ngxPermissionsGuard, CanAccessPersonDetailGuard],
        data: {
          breadcrumb: 'shared.termination_of_services',
          permissions: {
            only: AuthorizationUtil.GROUP_OFFICE,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'termination-of-services',
            data: {
              breadcrumb: 'shared.termination_of_services'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/termination-of-service-list-page/termination-of-service-list-page.component').then(
                    m => m.TerminationOfServiceListPageComponent
                  ),
                data: {
                  breadcrumb: 'shared.termination_of_services'
                }
              },
              {
                path: 'detail/:termination-of-service-id',
                loadComponent: () =>
                  import('~shared/education/pages/termination-of-service-detail-page/termination-of-service-detail-page.component').then(
                    m => m.TerminationOfServiceDetailPageComponent
                  ),
                canActivate: [detailGuard('termination-of-service-id')],
                resolve: { crud: crudResolver('termination-of-service-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.termination_of_service'
                }
              }
            ]
          },
          {
            path: 'service-contracts',
            data: {
              breadcrumb: 'shared.service_contracts'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/service-contract-list-page/service-contract-list-page.component').then(
                    m => m.ServiceContractListPageComponent
                  )
              },
              {
                path: 'detail/:service-contract-id',
                loadComponent: () =>
                  import('~shared/education/pages/service-contract-detail-page/service-contract-detail-page.component').then(
                    m => m.ServiceContractDetailPageComponent
                  ),
                canActivate: [detailGuard('service-contract-id')],
                resolve: { crud: crudResolver('service-contract-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.service_contract'
                }
              }
            ]
          },
          {
            path: 'declaration-of-commitment-il-pds',
            data: {
              breadcrumb: 'shared.declaration_of_commitment_il_pds'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/declaration-of-commitment-il-pd-list-page/declaration-of-commitment-il-pd-list-page.component').then(
                    m => m.DeclarationOfCommitmentIlPdListPageComponent
                  )
              },
              {
                path: 'detail/:declaration-of-commitment-il-pd-id',
                loadComponent: () =>
                  import('~shared/education/pages/declaration-of-commitment-il-pd-detail-page/declaration-of-commitment-il-pd-detail-page.component').then(
                    m => m.DeclarationOfCommitmentIlPdDetailPageComponent
                  ),
                canActivate: [detailGuard('declaration-of-commitment-il-pd-id')],
                resolve: { crud: crudResolver('declaration-of-commitment-il-pd-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.declaration_of_commitment_il_pd'
                }
              }
            ]
          },
          {
            path: 'igaza-certificates',
            data: {
              breadcrumb: 'shared.igaza_certificates'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/igaza-certificate-list-page/igaza-certificate-list-page.component').then(
                    m => m.IgazaCertificateListPageComponent
                  )
              },
              {
                path: 'detail/:igaza-certificate-id',
                loadComponent: () =>
                  import('~shared/education/pages/igaza-certificate-detail-page/igaza-certificate-detail-page.component').then(
                    m => m.IgazaCertificateDetailPageComponent
                  ),
                canActivate: [detailGuard('igaza-certificate-id')],
                resolve: { crud: crudResolver('igaza-certificate-id') },
                data: {
                  breadcrumb: 'detail_value',
                  breadcrumbParamValue: 'shared.igaza_certificate'
                }
              }
            ]
          }
        ]
      },
      /******************
       * ADMINISTRATION
       ******************/
      {
        path: 'administration',
        canActivate: [ngxPermissionsGuard],
        data: {
          breadcrumb: 'shared.administration',
          permissions: {
            only: AuthorizationUtil.GROUP_OFFICE,
            redirectTo: '/'
          }
        },
        children: [
          {
            path: 'communities',
            data: {
              breadcrumb: 'shared.communities',
              icon: 'pi pi-building-columns'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/community-administration-page/community-administration-page.component').then(
                    m => m.CommunityAdministrationPageComponent
                  )
              },
              {
                path: 'status',
                data: {
                  breadcrumb: 'status'
                },
                children: [
                  {
                    path: ':status-id',
                    loadComponent: () =>
                      import('./pages/create-update-status-form-page/create-update-status-form-page.component').then(
                        m => m.CreateUpdateStatusFormPageComponent
                      ),
                    canActivate: [crudGuard('status-id')],
                    resolve: {
                      crud: crudResolver('status-id'),
                      breadcrumb: breadcrumbResolver('status-id')
                    },
                    data: {
                      breadcrumbParamValue: 'status'
                    }
                  }
                ]
              }
            ]
          },
          {
            path: 'actors',
            data: {
              breadcrumb: 'shared.actors'
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/actor-administration-page/actor-administration-page.component').then(
                    m => m.ActorAdministrationPageComponent
                  )
              }
            ]
          }
        ]
      },
      /******************
       * NOT FOUND
       ******************/
      {
        path: 'not-found',
        loadComponent: () =>
          import('~shared/shared/pages/page-not-found-page/page-not-found-page.component').then(
            m => m.PageNotFoundPageComponent
          ),
        data: {
          breadcrumb: 'page_not_found'
        }
      },
      { path: '**', redirectTo: 'not-found' }
    ]
  }
];
